import { Telemetry } from '../hooks/useTelemetry';

/**
 * Messages browsers use when `fetch()` fails before an HTTP response
 * (blocked by Brave Shields / an ad blocker, offline, DNS). Chrome and Brave
 * say "Failed to fetch"; WebKit says "Load failed"; Firefox says
 * "NetworkError when attempting to fetch resource."
 */
const NETWORK_FETCH_MESSAGES = new Set([
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
]);

type LocationLike = {
  assign: (url: string) => void;
  pathname: string;
  search: string;
  hash: string;
  href: string;
};

/**
 * True for the plain network TypeError Next's pages router does NOT mark as
 * an asset-load error. Those fall through `handleRouteInfoError` and render
 * `/_error` instead of hard-navigating. Cancellation and "Route Cancelled"
 * are a different Error and must not match.
 */
export function isNetworkFetchTypeError(error: unknown): error is TypeError {
  if (!(error instanceof TypeError)) return false;
  if ((error as TypeError & { cancelled?: boolean }).cancelled) return false;
  return NETWORK_FETCH_MESSAGES.has(error.message);
}

function isSafeInternalPath(url: string): boolean {
  if (!url.startsWith('/') || url.startsWith('//') || url.startsWith('/\\')) return false;
  return !/[\\\u0000-\u001f]/.test(url);
}

function isAlreadyHere(url: string, location: LocationLike): boolean {
  if (url === location.href) return true;
  const current = `${location.pathname}${location.search}${location.hash}`;
  if (url === current) return true;
  const strip = (value: string) => (value.length > 1 ? value.replace(/\/$/, '') : value);
  return strip(url) === strip(current);
}

/**
 * `routeChangeError` companion to the dog page in `pages/_error.tsx`.
 *
 * Next 12.3.7 only emits this event for asset-load failures (which it
 * already hard-navigates) and for cancellations. A `/_next/data` TypeError
 * usually skips the event and renders `/_error` — the dog page covers that.
 * If a network TypeError does come through the event, report it and load
 * `url` as a document so the blocked `/_next/data` request is not retried
 * by the client router.
 *
 * Same-URL assigns are skipped: reloading the page the router already
 * updated to is the Try again button's job, and assigning it here can loop
 * if the event fires again for the current location.
 */
export function handleRouteDataFetchError(
  error: unknown,
  url: string,
  location: LocationLike = window.location,
): void {
  if (!isNetworkFetchTypeError(error)) return;

  try {
    const appVersion = process.env.NEXT_PUBLIC_VERSION || 'unknown';
    Telemetry()?.noticeError(error, {
      appVersion,
      source: 'routeChangeError',
      url,
    });
  } catch {
    // Reporting must not block the navigation off the broken transition.
  }

  if (!isSafeInternalPath(url) || isAlreadyHere(url, location)) return;

  try {
    location.assign(url);
  } catch {
    // The dog page stays up; Try again reloads.
  }
}
